import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useChatStore } from '../../store/chatStore';
import { useAuthStore } from '../../store/authStore';
import { RequestState } from '../../types';
import { detectContactInfo } from '../../utils';
import config from '../../config';

export const SurveyorChatScreen: React.FC = () => {
  const { negotiationId } = useParams<{ negotiationId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { messages, negotiation, isLoading, isSending, isConnected, loadNegotiation, loadMessages, sendMessage, connectWS, disconnectWS, clearChat } = useChatStore();

  const [text, setText] = useState('');
  const [warning, setWarning] = useState('');
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!negotiationId) return;
    loadNegotiation(negotiationId);
    loadMessages(negotiationId);
    connectWS(negotiationId);
    return () => { disconnectWS(); clearChat(); };
  }, [negotiationId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  // Fallback polling when WebSocket is disconnected
  useEffect(() => {
    if (!negotiationId || isConnected) return;
    const pollId = setInterval(() => loadMessages(negotiationId), config.CHAT_POLL_INTERVAL);
    return () => clearInterval(pollId);
  }, [isConnected, negotiationId]);

  const confirmed = negotiation?.status === RequestState.CONFIRMED || negotiation?.status === RequestState.CLOSED;
  const isClosed = negotiation?.status === RequestState.CLOSED;

  const handleSend = () => {
    if (!text.trim() || !negotiationId) return;

    if (!confirmed && detectContactInfo(text).hasContactInfo) {
      setWarning('Contact info is blocked until the deal is confirmed.');
      return;
    }
    setWarning('');
    sendMessage(negotiationId, text.trim());
    setText('');
  };

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <button onClick={() => navigate(-1)} style={s.backBtn} className="sc-press">
          ← Back
        </button>
        <div style={{ flex: 1 }}>
          <h2 style={s.title}>
            Chat with {negotiation?.client ? `${negotiation.client.first_name} ${negotiation.client.last_name}` : 'Client'}
          </h2>
          <div style={s.statusRow}>
            <span style={{ 
              ...s.statusDot, 
              background: isConnected ? 'var(--sc-success)' : 'var(--sc-error)' 
            }} />
            <span style={s.statusText}>{isConnected ? 'Connected' : 'Connecting...'}</span>
            {negotiation && (
              <span style={s.statusText}> · {negotiation.status.replace(/_/g, ' ')}</span>
            )}
          </div>
        </div>
      </div>

      {/* Restriction Banner */}
      {!confirmed && !isClosed && (
        <div style={s.restrictBanner}>
          <span style={{ marginRight: 6 }}>🔒</span>
          <span>Contact details are filtered until deal confirmation.</span>
        </div>
      )}

      {/* Messages */}
      <div ref={listRef} style={s.messageList} className="sc-animate-scale-in">
        {(messages ?? []).map((msg) => {
          const isSelf = msg.sender?.id === user?.id;
          const isSystem = msg.message_type === 'SYSTEM';
          const isBlocked = msg.is_blocked;

          if (isSystem) {
            return <div key={msg.id} style={s.systemMsg}>{msg.content}</div>;
          }
          
          if (isBlocked) {
            return (
              <div key={msg.id} style={s.blockedMsg}>
                <span>⚠️ Message blocked: contact details detected.</span>
              </div>
            );
          }

          return (
            <div 
              key={msg.id} 
              style={{
                ...s.bubble,
                ...(isSelf ? s.bubbleSelf : s.bubbleOther)
              }}
            >
              {!isSelf && <div style={s.senderName}>{msg.sender?.first_name}</div>}
              <div>{msg.content}</div>
              <div style={s.time}>
                {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Input / Warning */}
      {isClosed ? (
        <div style={s.closedBar}>This deal is closed. Chat is read-only.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {warning && (
            <div style={s.warningBar}>
              <span>⚠️ {warning}</span>
            </div>
          )}
          <div style={s.inputBar}>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder="Type a message..."
              style={s.input}
            />
            <button 
              onClick={handleSend} 
              disabled={!text.trim() || isSending} 
              style={{ 
                ...s.sendBtn, 
                background: text.trim() ? 'var(--sc-brand-gradient)' : 'var(--sc-bg-input)',
                color: text.trim() ? '#FFF' : 'var(--sc-text-tertiary)',
              }}
              className="sc-press"
            >
              {isSending ? '...' : 'Send'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: { 
    display: 'flex', 
    flexDirection: 'column', 
    height: 'calc(100vh - 120px)', 
    maxWidth: 720, 
    margin: '0 auto',
    fontFamily: 'var(--sc-font-family)',
    paddingBottom: 'env(safe-area-inset-bottom, 16px)',
  },
  header: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 },
  backBtn: { background: 'none', border: 'none', color: 'var(--sc-brand-primary)', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0 },
  title: { fontSize: 18, fontWeight: 800, color: 'var(--sc-text-primary)', margin: 0, fontFamily: 'var(--sc-font-display)' },
  statusRow: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 },
  statusDot: { width: 8, height: 8, borderRadius: '50%' },
  statusText: { fontSize: 11, color: 'var(--sc-text-secondary)', fontWeight: 500 },

  restrictBanner: { 
    background: 'var(--sc-warning-bg)', 
    borderRadius: 'var(--sc-radius-md)', 
    padding: '10px 14px', 
    fontSize: 12.5, 
    color: 'var(--sc-warning-text)', 
    fontWeight: 600, 
    marginBottom: 12,
    border: '1px solid var(--sc-warning)',
    display: 'flex',
    alignItems: 'center',
  },
  warningBar: { 
    background: 'var(--sc-error-bg)', 
    borderRadius: 'var(--sc-radius-md)', 
    padding: '10px 14px', 
    fontSize: 12.5, 
    color: 'var(--sc-error-text)', 
    marginBottom: 8,
    border: '1px solid var(--sc-error)',
    display: 'flex',
    alignItems: 'center',
  },
  closedBar: { background: 'var(--sc-bg-input)', borderRadius: 'var(--sc-radius-md)', padding: '14px 20px', fontSize: 13, color: 'var(--sc-text-secondary)', fontWeight: 600, textAlign: 'center', border: '1px solid var(--sc-border)' },

  messageList: { 
    flex: 1, 
    overflowY: 'auto', 
    background: 'var(--sc-bg-secondary)', 
    borderRadius: 'var(--sc-radius-lg)', 
    border: '1px solid var(--sc-border)', 
    boxShadow: 'var(--sc-shadow-card)', 
    padding: '16px 20px', 
    display: 'flex', 
    flexDirection: 'column', 
    gap: 8, 
    marginBottom: 12 
  },
  systemMsg: { textAlign: 'center', fontSize: 11, color: 'var(--sc-text-secondary)', padding: '6px 12px', background: 'var(--sc-bg-input)', borderRadius: 20, alignSelf: 'center', fontWeight: 600, border: '1px solid var(--sc-border-light)' },
  blockedMsg: { display: 'flex', alignItems: 'center', padding: '8px 14px', background: 'var(--sc-error-bg)', borderRadius: 'var(--sc-radius-md)', fontSize: 12, color: 'var(--sc-error-text)', alignSelf: 'center', border: '1px solid var(--sc-error)' },
  
  bubble: { maxWidth: '75%', padding: '10px 16px', borderRadius: 16, fontSize: 13.5, lineHeight: 1.5, position: 'relative' },
  bubbleSelf: { background: 'var(--sc-brand-primary-bg)', color: 'var(--sc-brand-primary)', alignSelf: 'flex-end', borderBottomRightRadius: 2, fontWeight: 500 },
  bubbleOther: { background: 'var(--sc-bg-input)', color: 'var(--sc-text-primary)', alignSelf: 'flex-start', borderBottomLeftRadius: 2, border: '1px solid var(--sc-border-light)' },
  senderName: { fontSize: 10, fontWeight: 700, color: 'var(--sc-brand-primary-light)', marginBottom: 4 },
  time: { fontSize: 10, color: 'var(--sc-text-tertiary)', textAlign: 'right', marginTop: 4, fontWeight: 500 },

  inputBar: { display: 'flex', gap: 10, alignItems: 'center' },
  input: { 
    flex: 1, 
    height: 'var(--sc-touch-input)', 
    padding: '0 16px', 
    border: '1px solid var(--sc-border)', 
    borderRadius: 24, 
    fontSize: 14, 
    outline: 'none', 
    fontFamily: 'inherit', 
    color: 'var(--sc-text-primary)', 
    background: 'var(--sc-bg-input)',
    transition: 'all var(--sc-transition-fast)'
  },
  sendBtn: { 
    height: 40,
    padding: '0 20px', 
    border: 'none', 
    borderRadius: 20, 
    fontSize: 13.5, 
    fontWeight: 700, 
    cursor: 'pointer', 
    fontFamily: 'inherit', 
    transition: 'all var(--sc-transition-fast)',
    boxShadow: 'var(--sc-shadow-sm)'
  },
};
